import { ChevronRightIcon } from "components/icons/Icons";
import { useTranslation } from "react-i18next";
import { NsKey } from "enums/NsKey";
import { PersonTypeEnum } from "api/actor";
import { CommercialIcon, ManIcon, WomanIcon } from "components/icons/customIcons";
import { ReactNode, useCallback } from "react";
import { SearchPersonResult } from "components/domain/SearchBar/types";

type SearchBarSize = 'sm' | 'md' | 'lg';

type PersonSearchBarItemProps = {
  person: SearchPersonResult;
  selected: boolean;
  size?: SearchBarSize;
}

const FEMALE_CIVILITIES = ['Mme', 'MME'] // Centralisé et extensible
const ICON_CLASS = "w-6 text-primary"


export const PersonSearchBarItem: React.FC<PersonSearchBarItemProps> = ({
  person,
  selected,
  size = 'lg'
}) => {
  const { t } = useTranslation(NsKey.ActorForm);

  const getGenderIcon = useCallback((code?: string) => {
    if (code && FEMALE_CIVILITIES.includes(code)) {
      return <WomanIcon className={ICON_CLASS} />
    }
    return <ManIcon className={ICON_CLASS} />
  }, [])

  const getIcon = (person: SearchPersonResult): ReactNode => {
    switch (person.personType) {
      case PersonTypeEnum.LegalEntity:
        return <CommercialIcon className={ICON_CLASS} />
      case PersonTypeEnum.NaturalPerson:
        return getGenderIcon(person.civility)
    }
  };

  // Helper function pour déterminer le type d'entité
  const getEntityTypeLabel = (person: SearchPersonResult): string => {
    switch (person.personType) {
      case PersonTypeEnum.LegalEntity:
        return t("ACTOR_FORM.LABELS.LEGAL_ENTITY");
      case PersonTypeEnum.NaturalPerson:
        return t("ACTOR_FORM.LABELS.NATURAL_PERSON");
      default:
        return t("ACTOR_FORM.LABELS.NATURAL_PERSON");
    }
  };

  // Version simplifiée pour sm et md - seulement icône et désignation
  if (size === 'sm' || size === 'md') {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 w-full min-w-0">
          <div className="flex-shrink-0">
            {getIcon(person)}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`${size === 'sm' ? 'text-xs' : 'text-sm'} font-medium truncate ${selected ? "font-semibold" : ""}`}>
              {person.name}
            </p>
            {/* Sous-titre : l'email quand la source en fournit un (courtiers…),
                sinon le type d'entité — plus utile pour distinguer des homonymes. */}
            <p className={`${size === 'sm' ? 'text-xs' : 'text-xs'} text-gray-500 truncate`}>
              {person.email || getEntityTypeLabel(person)}
            </p>
          </div>
        </div>
        <ChevronRightIcon
          className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} text-gray-400 group-data-[focus]:text-primary flex-shrink-0 ml-2`}
        />
      </div>
    );
  }

  // Version complète pour lg
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3 w-full">
        <div className="flex-shrink-0">
          {getIcon(person)}
        </div>
        <div className="flex grow text-left">
          <div className="flex justify-between items-center grow space-x-8 mr-4">
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${selected ? "font-semibold" : ""}`}>
                {person.name}
              </p>
              {person.address && (
                <p className="text-xs text-gray-500">
                  {person.address.line1}
                  {person.address.line2 && `, ${person.address.line2}`}
                  {person.address.zipCode && `, ${person.address.zipCode}`}
                  {person.address.city && ` ${person.address.city}`}
                </p>
              )}
            </div>
            <div className="flex items-center">
              {person.phone && (
                <span className="text-xs text-gray-500">
                  {person.phone}
                </span>
              )}
            </div>
            <div className="flex items-center">
              <span className="inline-flex rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                {getEntityTypeLabel(person)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <ChevronRightIcon
        className={`h-4 w-4 text-gray-400 group-data-[focus]:text-primary`}
      />
    </div>
  );
};
